import { Component, computed, effect, inject, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { TNil } from '@app/app.interface';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { EMatchingType } from '@enums/campaign-analytics.enum';
import { EEventType } from '@enums/event-tracking.enum';
import { ICtaResultApi } from '@interfaces/campaign-analytics-api.interface';
import { ICtaResult } from '@interfaces/campaign-analytics.interface';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { CampaignAnalyticsUtilitiesService } from '@utilities/campaign-analytics-utilities.service';
import { NgxFloatUiModule, NgxFloatUiPlacements, NgxFloatUiTriggers } from 'ngx-float-ui';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { ManualMatchingStatusListStore } from './manual-cta-result-matching-type-list.store';
import {
  EManualMatchingStatus,
  EManualMatchingStatusAction,
  IManualMatchingStatus,
  IManualMatchingStatusContext,
} from './manual-cta-result-matching-type.interface';
import { ACTION_STATUS_MAP } from './manual-cta-result-matching-type.service';
import { ManualMatchingStatusStore } from './manual-cta-result-matching-type.store';

@Component({
  imports: [
    MatMenuModule,
    MatButtonModule,
    NgxFloatUiModule,
    TranslateModule,
    DotsSpinnerComponent,
    IconComponent,
    NgxSkeletonLoaderModule,
  ],
  providers: [ManualMatchingStatusStore],
  selector: 'app-manual-cta-result-matching-type',
  templateUrl: './manual-cta-result-matching-type.component.html',
})
export class ManualCtaResultMatchingTypeComponent {
  public readonly ctaResult = input.required<ICtaResult | ICtaResultApi>();
  public readonly matchingLabelKey = input.required<string>();
  public readonly context = input.required<IManualMatchingStatusContext>();
  public readonly manualMatchingStatus = input.required<IManualMatchingStatus | TNil>();
  public readonly isSelected = input.required<boolean>();
  public readonly actionsVisible = input.required<boolean>();
  public readonly subheaderVisible = input.required<boolean>();
  public readonly textSize = input<'sm' | 'xs'>('sm');

  public readonly showRequested = output<string>();

  public readonly EManualMatchingStatusAction = EManualMatchingStatusAction;

  // Undefined means the list store hasn't resolved yet (still fetching).
  // If there's no socialContentId the fetch is skipped entirely — treat that as settled (null), not loading.
  // Not gated on isSelected: see-also CTAs also need their status loaded before the subheader is shown.
  public readonly isLoading = computed(
    () => this.manualMatchingStatus() === undefined && !!this.context().socialContentId,
  );

  public readonly effectiveManualMatchingStatus = computed<IManualMatchingStatus | TNil>(() => {
    const localResult = this.store.manualMatchingStatus$();

    // Only use the per-component store result if it actually belongs to the current CTA.
    // The component instance is reused when ctaResult changes, so the store may still hold
    // a result from the previous CTA until a new operation fires.
    if (localResult !== null && localResult.ctaTemplateId === this.ctaResult().ctaId) {
      return localResult;
    }

    return this.manualMatchingStatus();
  });

  public readonly currentStatus = computed<EManualMatchingStatus | TNil>(() => {
    return this.effectiveManualMatchingStatus()?.status;
  });

  public readonly subheaderKey = computed<string>(() => {
    const effective = this.effectiveManualMatchingStatus();
    const status = effective?.status;
    const { manualMatched } = this.ctaResult();

    if (status === null && manualMatched !== null) {
      // Explicit removal pending backend reprocessing.
      return 'shared.matchingActions.manualStatusRemovedRecalculating';
    }

    // Prefer the effective override status; fall back to manualMatched so read-only modes
    // (briefing, admin, export without detailed analytics) still reflect the actual manual
    // matching outcome rather than the generic "override possible/not possible" copy.
    const displayStatus = status ?? manualMatched;

    if (displayStatus === EManualMatchingStatus.matched) {
      if (manualMatched === EManualMatchingStatus.matched) {
        return this.actionsVisible()
          ? 'shared.matchingActions.manuallyMatchedOverridePossible'
          : 'shared.matchingActions.manuallyMatchedOverrideNotPossible';
      }

      return 'shared.matchingActions.manuallyMatchedRecalculating';
    }

    if (displayStatus === EManualMatchingStatus.unmatched) {
      if (manualMatched === EManualMatchingStatus.unmatched) {
        return this.actionsVisible()
          ? 'shared.matchingActions.manuallyUnmatchedOverridePossible'
          : 'shared.matchingActions.manuallyUnmatchedOverrideNotPossible';
      }

      return 'shared.matchingActions.manuallyUnmatchedRecalculating';
    }

    return this.actionsVisible()
      ? 'shared.matchingActions.manualOverridePossible'
      : 'shared.matchingActions.manualOverrideNotPossible';
  });

  public readonly menuOptions = computed<Array<{ label: string; action: EManualMatchingStatusAction }>>(() => {
    const status = this.currentStatus();
    const { matchingType } = this.ctaResult();
    const options: Array<{ label: string; action: EManualMatchingStatusAction }> = [];

    if (matchingType !== EMatchingType.matched && (!status || status === EManualMatchingStatus.unmatched)) {
      options.push({
        action: EManualMatchingStatusAction.markAsMatched,
        label: 'shared.matchingActions.markAsMatched',
      });
    }

    if (
      matchingType !== EMatchingType.bonus &&
      matchingType !== EMatchingType.unrelated &&
      (!status || status === EManualMatchingStatus.matched)
    ) {
      options.push({
        action: EManualMatchingStatusAction.markAsUnmatched,
        label: 'shared.matchingActions.markAsUnmatched',
      });
    }

    if (status) {
      options.push({
        action: EManualMatchingStatusAction.remove,
        label: 'shared.matchingActions.removeManualMatching',
      });
    }

    return options;
  });

  public readonly nameTextClass = computed(() => (this.textSize() === 'sm' ? 'text-sm' : 'text-xs'));
  public readonly subheaderTextClass = computed(() => (this.textSize() === 'sm' ? 'text-xs' : 'text-[10px]'));

  public readonly statusIconColor = computed(
    () => this.campaignAnalyticsUtilitiesService.getStatusableDisplayConfig(this.ctaResult().status).color,
  );

  public readonly hasStatusIndicatorIssues = computed(() => {
    const { ctaKpiStatus } = this.ctaResult();

    return (
      ctaKpiStatus.currentFailedDeliverables > 0 ||
      ctaKpiStatus.currentProblemDeliverables > 0 ||
      ctaKpiStatus.currentProblemKpis > 0 ||
      ctaKpiStatus.currentFailedKpis > 0
    );
  });

  public readonly floatUiTriggerHover = NgxFloatUiTriggers.hover;
  public readonly floatUiPlacement = NgxFloatUiPlacements.TOP;

  public readonly store = inject(ManualMatchingStatusStore);
  private readonly campaignAnalyticsUtilitiesService = inject(CampaignAnalyticsUtilitiesService);
  private readonly listStore = inject(ManualMatchingStatusListStore);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translate = inject(TranslateService);
  private readonly eventTrackingService = inject(EventTrackingService);

  constructor() {
    effect(() => {
      const loading = this.store.loading$();
      const result = this.store.manualMatchingStatus$();
      const error = this.store.error$();

      if (!loading && result !== null && !error && result.ctaTemplateId === this.ctaResult().ctaId) {
        const ctaTemplateId = this.ctaResult().ctaId;

        this.listStore.setSingleStatus({
          ctaTemplateId,
          status: result,
        });

        // Reset the per-component store so effectiveManualMatchingStatus defers to the
        // list-store input from here on. Without this the cached local result would keep
        // overriding fresher values delivered by the next list-store refresh.
        this.store.resetStatus();
        this.snackbarService.openSuccess(this.translate.instant('shared.matchingActions.success'));

        this.eventTrackingService.track(EEventType.campaignAnalyticsManualMatchingStatusSet, {
          campaignId: result.campaignId,
          contentStatus: this.ctaResult().status,
          manualStatus: result.status ?? undefined,
          matchingType: this.ctaResult().matchingType,
          socialContentId: result.socialContentId,
        });
      }
    });
  }

  public onMenuClick(action: EManualMatchingStatusAction, event: MouseEvent, menuTrigger: MatMenuTrigger): void {
    event.stopPropagation();
    menuTrigger.closeMenu();

    const { socialContentId } = this.context();

    if (!socialContentId) {
      return;
    }

    const effective = this.effectiveManualMatchingStatus();
    const newStatus: EManualMatchingStatus | null =
      action !== EManualMatchingStatusAction.remove ? ACTION_STATUS_MAP[action] : null;

    const payload: IManualMatchingStatus = {
      ...(effective?.id ? { id: effective.id } : {}),
      campaignId: this.context().campaignId,
      creatorId: this.context().creatorId,
      ctaTemplateId: this.ctaResult().ctaId,
      processed: false,
      socialContentId,
      status: newStatus,
    };

    if (effective?.id) {
      this.store.updateManualMatchingStatus({ ...payload, id: effective.id });
    } else {
      this.store.createManualMatchingStatus(payload);
    }
  }

  public onShowClick(): void {
    this.showRequested.emit(this.ctaResult().ctaId);
  }
}
